import React from 'react';
import {useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';

import {Button, Modal, ModalActions, ModalHeader, showModal} from '@edna/components';

import {SecondaryText} from 'src/components/primitives';
import {EPaymentType, useGetTenantDataQuery} from 'src/containers/User';

import TariffTemplatesList from '../TariffTemplatesList';
import {useAcceptTariffsMutation} from '../api';
import {TTariffTemplate} from '../definitions';

export const AcceptTariffModal = React.memo<{
  closeModal: TEmptyFunction;
  tariffTemplates: TTariffTemplate[];
}>(({closeModal, tariffTemplates}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const {data: tenantData} = useGetTenantDataQuery();

  const isPrePaymentType = tenantData?.paymentType === EPaymentType.PREPAYMENT;
  const [acceptTariffs] = useAcceptTariffsMutation();

  const handleSubmit = React.useCallback(async () => {
    if (tariffTemplates.length) {
      try {
        await acceptTariffs(tariffTemplates.map((tariff) => tariff.id)).unwrap();
      } catch {}
    }

    closeModal();
  }, [tariffTemplates, closeModal, dispatch]);

  return (
    <Modal width="582px">
      <ModalHeader>{t('Tariffs:acceptTariff.title')}</ModalHeader>
      <SecondaryText bottomIndent="6">
        {t('Tariffs:acceptTariff.subtitle', {
          buttonName: isPrePaymentType
            ? t('Tariffs:button.acceptTariff', {count: tariffTemplates.length})
            : t('Tariffs:button.fine'),
        })}
      </SecondaryText>
      <TariffTemplatesList tariffs={tariffTemplates} />
      <ModalActions>
        <Button
          appearance="primary"
          onClick={handleSubmit}
          data-selector="Tariffs:button.acceptTariff"
        >
          {isPrePaymentType
            ? t('Tariffs:button.acceptTariff', {count: tariffTemplates.length})
            : t('Tariffs:button.fine')}
        </Button>
      </ModalActions>
    </Modal>
  );
});

export const showAcceptTariffModal = (tariffTemplates: TTariffTemplate[]) =>
  showModal((closeModal) => (
    <AcceptTariffModal closeModal={closeModal} tariffTemplates={tariffTemplates} />
  ));
